import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { setHeader, setRoutes, setUsername } from '../core/ui/shell';
import { Link } from 'react-router-dom';
import { useIdentity } from 'src/core/services/authentication';
import { Menu, Card, Row, Spin } from 'antd';
import { UserOutlined, AuditOutlined, TeamOutlined, BankOutlined, FundOutlined, KeyOutlined } from '@ant-design/icons';
import { useCurrentUserPermissionCheckInMainOrg, useCurrentUserTenantPermissionsFilter } from 'src/common/permission-hooks';
import { IdentityType, PermissionsConstants } from 'src/common/types';
import { container } from 'src/inversify.config';
import { LicenseCreationAttributesStore } from 'src/stores/licensing-store';
import { theme } from 'antd';
const { useToken } = theme;

const permissions = [
  PermissionsConstants.CreateOrganization,
  PermissionsConstants.DeleteOrganization,
  PermissionsConstants.ManageOrganizationInfo,
  PermissionsConstants.BlockOrganization,

  PermissionsConstants.ManageUsers,
  PermissionsConstants.ManageRoles,
  PermissionsConstants.ManageGroups,

  PermissionsConstants.ManageLicenses,
  PermissionsConstants.ApproveLicenses,
  PermissionsConstants.ManagePartnerOrganization,
];

const HomePage: FC = () => {
  const identity = useIdentity();
  const { t } = useTranslation();
  const getName = () => {
    return `${identity?.profile['display_name'] ?? ''}`.trimStart().trimEnd();
  };

  const cardIconStyle = { width: 48, height: 48, borderRadius: 8, backgroundColor: '#AB162B', margin: '0 141px', marginTop: '36px' };
  const { token } = useToken();
  const { colorPrimary, colorLink } = token;

  const iconStyle = { color: colorPrimary, margin: 13 };
  const linkStyle = { color: colorLink };

  setHeader(t('WelcomBackParam').replace('{0}', `${getName()}!`));
  setRoutes([]);
  setUsername(getName());

  const [userPermissionsSet, checkingPermissions] = useCurrentUserTenantPermissionsFilter(identity, permissions);
  const licenseAttributesStore = container.get(LicenseCreationAttributesStore);
  const [licenseManagerRoleValid, setLicenseManagerRoleValid] = useState(false);
  const [hasGlobalLicenseManagerPermission, checkingGlobalLicenseManagerPermission] = useCurrentUserPermissionCheckInMainOrg(
    identity,
    PermissionsConstants.ManageGlobalLicenses
  );

  useEffect(() => {
    const validateLicenseManagerRole = async () => {
      const roleValid = await licenseAttributesStore.getByUserScope({
        identityType: IdentityType.User,
        identityId: identity.profile['sub'],
        scopeId: identity.profile['tenant_id'],
      });
      setLicenseManagerRoleValid(roleValid);
    };

    validateLicenseManagerRole();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (checkingPermissions || checkingGlobalLicenseManagerPermission) {
    return (
      <Row align="top" justify="space-between">
        <Spin spinning size="large" style={{ maxHeight: '75vh' }}>
          <Card style={{ width: '80vw', height: '75vh' }}></Card>
        </Spin>
      </Row>
    );
  }

  return (
    <>
      <div>
        <Menu mode="vertical" style={{ backgroundColor: 'transparent' }}>
          {userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
            <Menu.Item className="main-page-button">
              <Card style={cardIconStyle}>
                <BankOutlined style={iconStyle} />
              </Card>
              <div>
                <Link style={linkStyle} to={`organizations`}>
                  {t('OrganizationsManagement')}
                </Link>
              </div>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageUsers) && userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
            <Menu.Item className="main-page-button">
              <Card style={cardIconStyle}>
                <UserOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`users`}>
                {t('UsersManagement')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageRoles) && userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
            <Menu.Item className="main-page-button">
              <Card style={cardIconStyle}>
                <AuditOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`roles`}>
                {t('RolesManagement')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageRoles) && userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
            <Menu.Item className="main-page-button">
              <Card style={cardIconStyle}>
                <AuditOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`partnerroles`}>
                {t('PartnerRolesManagement')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManageGroups) && userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
            <Menu.Item className="main-page-button">
              <Card style={cardIconStyle}>
                <TeamOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`groups`}>
                {t('GroupsManagement')}
              </Link>
            </Menu.Item>
          )}
          {((userPermissionsSet.has(PermissionsConstants.ManageLicenses) && licenseManagerRoleValid) ||
            hasGlobalLicenseManagerPermission ||
            userPermissionsSet.has(PermissionsConstants.ApproveLicenses)) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <KeyOutlined style={iconStyle} />
              </Card>
              <Link style={linkStyle} to={`licenses`}>
                {t('Licenses')}
              </Link>
            </Menu.Item>
          )}
          {userPermissionsSet.has(PermissionsConstants.ManagePartnerOrganization) && (
            <Menu.Item className="main-page-button">
              <Card className="card-icon">
                <FundOutlined style={iconStyle} />
              </Card>
              <div>
                <Link style={linkStyle} to={`statistics`}>
                  {t('Statistics')}
                </Link>
              </div>
            </Menu.Item>
          )}
        </Menu>
      </div>
    </>
  );
};

export default HomePage;
